import { Fragment } from 'react';
import { Box, Table, IconButton, Flex, Text } from '@chakra-ui/react';
import { HiChevronUp, HiChevronDown, HiChevronRight } from 'react-icons/hi2';
import { ColumnFilterPopover, type FilterOption } from './ColumnFilterPopover';

export type SortDirection = 'asc' | 'desc' | null;

export interface Column<T> {
  key: string;
  label: string;
  sortable?: boolean;
  filterable?: boolean;
  filterOptions?: FilterOption[];
  render?: (row: T) => React.ReactNode;
  width?: string;
  maxWidth?: string;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  sortColumn?: string;
  sortDirection?: SortDirection;
  onSort?: (column: string) => void;
  filterValues?: Record<string, string>;
  onFilterChange?: (column: string, value: string) => void;
  onFilterClear?: (column: string) => void;
  onRowClick?: (row: T) => void;
  keyExtractor: (row: T) => string;
  renderExpandedRow?: (row: T) => React.ReactNode | null;
  /** Callback to provide custom props (bg, _hover, data-e2e) per row */
  getRowProps?: (row: T) => React.HTMLAttributes<HTMLTableRowElement> & Record<string, unknown>;
  /** Render an actions cell for each row */
  renderRowActions?: (row: T) => React.ReactNode;
  /** Position of the actions column: 'start' (left) or 'end' (right). Default: 'end' */
  actionsPosition?: 'start' | 'end';
  /** Key of the currently selected row (matched against keyExtractor) */
  selectedKey?: string;
  /** Keys of the currently selected rows (matched against keyExtractor) */
  selectedRowIds?: string[];
  /** Called when row selection changes */
  onSelectionChange?: (rows: T[]) => void;
  /** Accessible label for the table */
  caption?: string;
  /** Message rendered in table body when data is empty */
  emptyMessage?: string;
}

/**
 * @useFor the canonical tabular data view: sortable columns, optional
 *   per-column filtering, optional row selection, optional row action
 *   menu (via `RowActionsMenu` in the last column).
 *
 *   Examples in-tree: SMS People page, Onboarding job list, Reports
 *   incident table, Roster student list.
 *
 *   For a plain non-interactive layout grid use Chakra's `<Table>`
 *   directly. For a virtualised list (>1000 rows) use a different
 *   primitive — DataTable renders all rows. For a card-grid use
 *   `<SectionCard>` repeated, not DataTable.
 *
 *   Toolbars above the table (search, filter buttons, export) go in
 *   `<TableToolbar>`; pagination below goes in `<Pagination>`.
 */
export function DataTable<T extends object>({
  columns,
  data,
  sortColumn,
  sortDirection,
  onSort,
  filterValues,
  onFilterChange,
  onFilterClear,
  onRowClick,
  keyExtractor,
  renderExpandedRow,
  getRowProps,
  renderRowActions,
  actionsPosition = 'end',
  selectedKey,
  selectedRowIds = [],
  onSelectionChange,
  caption,
  emptyMessage = 'No data found',
}: DataTableProps<T>) {
  const isMultiSelect = !!onSelectionChange;

  const handleSelectAll = (checked: boolean) => {
    if (onSelectionChange) {
      onSelectionChange(checked ? [...data] : []);
    }
  };

  const handleSelectRow = (row: T, checked: boolean) => {
    if (!onSelectionChange) return;
    const rowKey = keyExtractor(row);
    const nextKeys = checked
      ? new Set([...selectedRowIds, rowKey])
      : new Set(selectedRowIds.filter((id) => id !== rowKey));
    onSelectionChange(data.filter((r) => nextKeys.has(keyExtractor(r))));
  };

  const handleHeaderClick = (column: Column<T>) => {
    if (column.sortable && onSort) {
      onSort(column.key);
    }
  };

  const handleHeaderKeyDown = (e: React.KeyboardEvent, column: Column<T>) => {
    if ((e.key === 'Enter' || e.key === ' ') && column.sortable && onSort) {
      e.preventDefault();
      onSort(column.key);
    }
  };

  const getAriaSortValue = (
    column: Column<T>,
  ): 'ascending' | 'descending' | 'none' | undefined => {
    if (!column.sortable) return undefined;
    if (sortColumn !== column.key || !sortDirection) return 'none';
    return sortDirection === 'asc' ? 'ascending' : 'descending';
  };

  const totalColumnCount =
    columns.length +
    (isMultiSelect ? 1 : 0) +
    (renderRowActions ? 1 : 0) +
    (onRowClick && !renderExpandedRow && !renderRowActions ? 1 : 0);

  return (
    <Box overflow="hidden" bg="surface.primary">
      <Table.Root size="sm" aria-label={caption}>
        <Table.Header>
          <Table.Row bg="surface.secondary">
            {isMultiSelect && (
              <Table.ColumnHeader width="40px" scope="col">
                <Flex align="center" justify="center">
                  <input
                    type="checkbox"
                    checked={data.length > 0 && selectedRowIds.length === data.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    aria-label="Select all rows"
                    style={{ cursor: 'pointer' }}
                  />
                </Flex>
              </Table.ColumnHeader>
            )}
            {renderRowActions && actionsPosition === 'start' && (
              <Table.ColumnHeader
                width="50px"
                scope="col"
                position="sticky"
                left={0}
                zIndex={2}
                bg="surface.secondary"
                boxShadow="1px 0 0 0 var(--chakra-colors-border-primary)"
              />
            )}
            {columns.map((column) => (
              <Table.ColumnHeader
                key={column.key}
                scope="col"
                width={column.width}
                maxWidth={column.maxWidth}
                cursor={column.sortable ? 'pointer' : 'default'}
                onClick={() => handleHeaderClick(column)}
                onKeyDown={(e: React.KeyboardEvent) =>
                  handleHeaderKeyDown(e, column)
                }
                _hover={column.sortable ? { bg: 'surface.hover' } : undefined}
                userSelect="none"
                aria-sort={getAriaSortValue(column)}
                role={column.sortable ? 'columnheader button' : 'columnheader'}
                tabIndex={column.sortable ? 0 : undefined}
                data-e2e={`header-${column.key}`}
              >
                <Flex align="center" gap={1} justify="space-between">
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="text.secondary"
                    textTransform="uppercase"
                  >
                    {column.label}
                  </Text>
                  <Flex align="center" gap={0}>
                    {column.sortable && sortColumn === column.key && (
                      <Box color="text.secondary">
                        {sortDirection === 'asc' ? (
                          <HiChevronUp size={14} />
                        ) : (
                          <HiChevronDown size={14} />
                        )}
                      </Box>
                    )}
                    {column.filterable && onFilterChange && (
                      <ColumnFilterPopover
                        columnKey={column.key}
                        currentValue={filterValues?.[column.key]}
                        options={column.filterOptions}
                        onApply={onFilterChange}
                        onClear={
                          onFilterClear ?? ((col) => onFilterChange(col, ''))
                        }
                      />
                    )}
                  </Flex>
                </Flex>
              </Table.ColumnHeader>
            ))}
            {renderRowActions && actionsPosition === 'end' && (
              <Table.ColumnHeader
                width="50px"
                scope="col"
                position="sticky"
                right={0}
                zIndex={2}
                bg="surface.secondary"
                boxShadow="-1px 0 0 0 var(--chakra-colors-border-primary)"
              />
            )}
            {onRowClick && !renderExpandedRow && !renderRowActions && (
              <Table.ColumnHeader width="50px" scope="col" />
            )}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {data.length === 0 ? (
            <Table.Row borderBottomWidth="1px" borderColor="border.primary">
              <Table.Cell colSpan={totalColumnCount} py={8} textAlign="center">
                <Text color="text.secondary" fontSize="sm">
                  {emptyMessage}
                </Text>
              </Table.Cell>
            </Table.Row>
          ) : (
            data.map((row, index) => {
              const expandedContent = renderExpandedRow?.(row);
              const rowKey = keyExtractor(row);
              const isSelected = selectedKey != null && rowKey === selectedKey;
              const isClickable = !!(onRowClick || onSelectionChange);
              const customRowProps = getRowProps?.(row) ?? {};
              const selectedBg = isSelected ? 'surface.select' : undefined;
              return (
                <Fragment key={rowKey}>
                  <Table.Row
                    cursor={isClickable ? 'pointer' : 'default'}
                    onClick={() => {
                      onRowClick?.(row);
                      if (isMultiSelect) {
                        handleSelectRow(row, !selectedRowIds.includes(rowKey));
                      }
                    }}
                    bg={selectedBg}
                    _hover={isClickable ? { bg: 'surface.hover' } : undefined}
                    borderBottomWidth="1px"
                    borderColor="border.primary"
                    transition="background 200ms"
                    data-e2e={`row-${index + 1}`}
                    {...customRowProps}
                  >
                    {isMultiSelect && (
                      <Table.Cell
                        py={4}
                        onClick={(e) => e.stopPropagation()}
                        textAlign="center"
                      >
                        <input
                          type="checkbox"
                          checked={selectedRowIds.includes(rowKey)}
                          onChange={(e) => handleSelectRow(row, e.target.checked)}
                          aria-label="Select row"
                          style={{ cursor: 'pointer' }}
                        />
                      </Table.Cell>
                    )}
                    {renderRowActions && actionsPosition === 'start' && (
                      <Table.Cell
                        py={4}
                        onClick={(e) => e.stopPropagation()}
                        position="sticky"
                        left={0}
                        zIndex={1}
                        bg="inherit"
                        boxShadow="1px 0 0 0 var(--chakra-colors-border-primary)"
                      >
                        {renderRowActions(row)}
                      </Table.Cell>
                    )}
                    {columns.map((column) => (
                      <Table.Cell
                        key={column.key}
                        py={4}
                        maxWidth={column.maxWidth}
                      >
                        {column.render ? (
                          column.render(row)
                        ) : (
                          <Text fontSize="sm" color="text.primary">
                            {
                              (row as Record<string, unknown>)[
                                column.key
                              ] as React.ReactNode
                            }
                          </Text>
                        )}
                      </Table.Cell>
                    ))}
                    {renderRowActions && actionsPosition === 'end' && (
                      <Table.Cell
                        py={4}
                        onClick={(e) => e.stopPropagation()}
                        position="sticky"
                        right={0}
                        zIndex={1}
                        bg="inherit"
                        boxShadow="-1px 0 0 0 var(--chakra-colors-border-primary)"
                      >
                        {renderRowActions(row)}
                      </Table.Cell>
                    )}
                    {onRowClick && !renderExpandedRow && !renderRowActions && (
                      <Table.Cell>
                        <IconButton
                          aria-label="View details"
                          variant="ghost"
                          size="xs"
                          colorScheme="gray"
                          data-e2e={`view-${index + 1}`}
                        >
                          <HiChevronRight size={16} />
                        </IconButton>
                      </Table.Cell>
                    )}
                  </Table.Row>
                  {expandedContent && (
                    <Table.Row
                      bg="surface.primary"
                      _hover={{ bg: 'surface.primary' }}
                      borderBottomWidth="1px"
                      borderColor="border.primary"
                    >
                      <Table.Cell colSpan={columns.length + (isMultiSelect ? 1 : 0) + (renderRowActions ? 1 : 0)} p={0}>
                        {expandedContent}
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Fragment>
              );
            })
          )}
        </Table.Body>
      </Table.Root>
    </Box>
  );
}
